import {Action, NgxsOnInit, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Auth} from '../actions/auth.actions';
import {Settings} from '../actions/settings.actions';
import UpdateExpansionPanel = Settings.UpdateExpansionPanel;
import UpdateDarkMode = Settings.UpdateDarkMode;
import UpdateSearchColumns = Settings.UpdateSearchColumns;
import UpdateJobStatusColumns = Settings.UpdateJobStatusColumns;
import UpdateStickyHeader = Settings.UpdateStickyHeader;

export const DEFAULT_JOB_STATUS_COLUMNS: string[] = [
  'number',
  'invoiceNumber',
  'debtor',
  'dateIn',
  'status',
  'latestComment'
];

export interface SettingsStateModel {
  expansionPanels: {
    [key: string]: boolean
  };
  searchColumns: string[];
  jobStatusColumns: string[];
  darkMode: boolean;
  stickyHeader: boolean;
}

@State<SettingsStateModel>({
  name: 'settings',
  defaults: {
    expansionPanels: {},
    searchColumns: [
      'type',
      'number',
      'invoiceNumber',
      'debtor',
      'metadata',
      'dateIn',
      'dateOut',
      'createdBy'
    ],
    jobStatusColumns: DEFAULT_JOB_STATUS_COLUMNS,
    darkMode: false,
    stickyHeader: true
  }
})
@Injectable()
export class SettingsState implements NgxsOnInit {
  constructor() {
  }

  /**
   * The settings slice is persisted as a whole, so a browser that stored it before a key existed
   * hydrates without that key and silently loses its default.
   */
  ngxsOnInit(ctx: StateContext<SettingsStateModel>): void {
    if (!ctx.getState().jobStatusColumns) {
      ctx.patchState({jobStatusColumns: DEFAULT_JOB_STATUS_COLUMNS});
    }
  }

  @Selector()
  static expansionPanels(state: SettingsStateModel): {
    [key: string]: boolean
  } {
    return state.expansionPanels;
  }

  @Selector()
  static darkMode(state: SettingsStateModel): boolean {
    return state.darkMode;
  }

  @Selector()
  static stickyHeader(state: SettingsStateModel): boolean {
    return state.stickyHeader;
  }

  @Selector()
  static searchColumns(state: SettingsStateModel): string[] {
    return state.searchColumns;
  }

  @Action(UpdateExpansionPanel)
  updateExpansionPanel(ctx: StateContext<SettingsStateModel>, action: UpdateExpansionPanel): void {
    ctx.patchState({
      expansionPanels: {
        ...ctx.getState().expansionPanels,
        [action.key]: action.enabled
      },
    });
  }

  @Selector()
  static jobStatusColumns(state: SettingsStateModel): string[] {
    return state.jobStatusColumns;
  }

  @Action(UpdateSearchColumns)
  updateSearchColumns(ctx: StateContext<SettingsStateModel>, action: UpdateSearchColumns): void {
    ctx.patchState({
      searchColumns: action.columns,
    });
  }

  @Action(UpdateJobStatusColumns)
  updateJobStatusColumns(ctx: StateContext<SettingsStateModel>, action: UpdateJobStatusColumns): void {
    ctx.patchState({
      jobStatusColumns: action.columns,
    });
  }

  @Action(UpdateDarkMode)
  updateDarkMode(ctx: StateContext<SettingsStateModel>, action: UpdateDarkMode): void {
    ctx.patchState({
      darkMode: action.enabled
    });
  }

  @Action(UpdateStickyHeader)
  updateStickHeader(ctx: StateContext<SettingsStateModel>, action: UpdateStickyHeader): void {
    ctx.patchState({
      stickyHeader: action.enabled
    });
  }

  /*@Action(ClearState)
  clearState(ctx: StateContext<SettingsStateModel>): void {
    ctx.setState({
      expansionPanels: {},
      darkMode: false
    });
  }*/
}
